<template>
  <v-dialog v-model="dialog" width="800" :retain-focus="false" persistent scrollable @click:outside="closeDialog">
    <v-card class="custom-card" rounded="0">
      <v-toolbar flat color="primary" class="white--text">
        <v-toolbar-title class="font-weight-bold">{{ $t('userslist.createNewUser') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="black--text">
        <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
          <!-- Membership Plan (access level) + Activation Key -->
          <div class="mb-2">
            <v-row>
              <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
                <label>{{ $t('activationKey.membershipPlan') }}<span class="red--text">*</span></label>
                <v-select v-model="user.membershipPlan" :items="factoryPlanItems" outlined dense
                  :placeholder="$t('activationKey.membershipPlan')" :rules="rules.membershipPlan"></v-select>
              </div>
              <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
                <label>{{ $t('activationKey.activationKey') }}</label>
                <v-text-field v-model="user.activationKey" :placeholder="$t('activationKey.activationKey')"
                  outlined dense></v-text-field>
                <div class="d-flex justify-end">
                  <v-btn small color="primary" :loading="keyLoading" @click="generateKey">
                    {{ $t('userslist.generateKey') }}
                  </v-btn>
                </div>
              </div>
            </v-row>
          </div>

          <v-row>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('firstName') }}<span class="red--text">*</span></label>
              <v-text-field v-model="user.firstName" :placeholder="$t('firstName')" outlined dense :rules="rules.firstName"></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('lastName') }}<span class="red--text">*</span></label>
              <v-text-field v-model="user.lastName" :placeholder="$t('lastName')" outlined dense :rules="rules.lastName"></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('emailId') }}</label>
              <v-text-field v-model="user.email" :placeholder="$t('emailId')" outlined dense :error-messages="errors.email"></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('mobileNumber') }}</label>
              <v-text-field v-model="user.mobile" :placeholder="$t('mobile')" outlined dense :error-messages="errors.mobile"></v-text-field>
            </div>

            <v-col cols="6" class="py-0">
              <label>{{ $t('country') }}<span class="red--text">*</span></label>
              <v-autocomplete outlined dense v-model="user.countryId" :items="countries" item-value="name" item-text="name"
                :rules="rules.countryId" :placeholder="$t('country')" return-object @change="getState">
                <template v-slot:item="slotProps">
                  <i :class="['mr-2', 'em', `em-flag-${slotProps.item.code.toLowerCase()}`]"></i>{{ slotProps.item.name }}
                </template>
                <template slot="selection" slot-scope="data">
                  <i :class="['mr-2', 'em', `em-flag-${data.item.code.toLowerCase()}`]"></i>{{ data.item.name }}
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6" class="py-0">
              <label>{{ $t(`${isKenyaClient() ? 'County' : 'provinceState'}`) }}<span class="red--text">*</span></label>
              <v-autocomplete outlined dense v-model="user.stateId" :items="states" item-value="name" item-text="name"
                :rules="rules.state" :placeholder="$t(`${isKenyaClient() ? 'County' : 'provinceState'}`)" return-object></v-autocomplete>
            </v-col>

            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('regionDistrict') }}</label>
              <v-text-field v-model="user.district" :placeholder="$t('regionDistrict')" outlined dense></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('cityTown') }}</label>
              <v-text-field v-model="user.village" :placeholder="$t('cityTown')" outlined dense></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('address') }}</label>
              <v-text-field v-model="user.address" :placeholder="$t('address')" outlined dense></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('familyTribe') }}</label>
              <v-text-field v-model="user.userTribe" placeholder="Enter your family tribe" outlined dense></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('website') }}</label>
              <v-text-field v-model="user.website" placeholder="Enter website" outlined dense></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('addEdituser.validityUpTo') }}</label>
              <v-text-field v-model="user.validityUpTo" outlined disabled dense></v-text-field>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('status') }}<span class="red--text">*</span></label>
              <v-radio-group v-model="user.status" row>
                <v-radio label="Active" :value="1"></v-radio>
                <v-radio label="Deactivated" :value="0"></v-radio>
              </v-radio-group>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0">
              <label>{{ $t('roleRequest.userRole') }}</label>
              <v-select :items="userRoleItems" v-model="user.userRole" outlined dense disabled></v-select>
            </div>
            <div class="col-12 py-0 mt-2">
              <v-btn color="primary" outlined @click="isSetPasswordDialogOpen = true">
                {{ $t('memberDataSection.setTemporaryPassword') }}
              </v-btn>
              <span v-if="user.password" class="ml-3 green--text caption">✓</span>
            </div>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="px-6 py-3">
        <v-spacer></v-spacer>
        <v-btn @click="closeDialog" outlined right height="44" width="116" depressed color="primary">{{ $t('cancel') }}</v-btn>
        <v-btn @click="saveUser" right height="44" width="116" color="primary">{{ $t('submit') }}</v-btn>
      </v-card-actions>
    </v-card>
    <TemporaryPasswordDialog v-model="isSetPasswordDialogOpen" @passwordSet="onPasswordSetFromDialog" @cancel="onPasswordSetCancel"></TemporaryPasswordDialog>
  </v-dialog>
</template>

<script>
import { getCountries, getStates } from 'country-state-picker';
import { isKenyaClient } from '@/utils';
import { kenyaCounties } from '@/constants/countries.js';
import UserService from '@/_services/UserService';
import BuyingStationService from '@/_services/BuyingStationService';
import TemporaryPasswordDialog from '@/views/user/naccu-user/TemporaryPasswordDialog.vue';
import LoadingMixin from '@/mixins/LoadingMixin';
import generalMixin from '@/mixins/GeneralMixin';

export default {
  name: 'AddFactoryUser',
  components: { TemporaryPasswordDialog },
  mixins: [LoadingMixin, generalMixin],
  props: {
    dialog: { type: Boolean, default: false },
    buyingStationId: { type: [String, Number], default: null },
  },
  data() {
    return {
      valid: true,
      keyLoading: false,
      isSetPasswordDialogOpen: false,
      countries: [],
      states: [],
      userRoleItems: ['Farmer'],
      user: {
        membershipPlan: '',
        activationKey: '',
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        countryId: null,
        stateId: null,
        district: '',
        village: '',
        address: '',
        userTribe: '',
        website: '',
        validityUpTo: '',
        status: 1,
        userRole: 'Farmer',
        password: '',
      },
      errors: { email: '', mobile: '' },
      rules: {
        membershipPlan: [v => !!v || 'Membership Plan is required.'],
        firstName: [
          v => !!v || this.$t('addEdituser.firstNameRequired'),
          v => this.onlyAlpha(v) || this.$t('addEdituser.firstNameOllyAlpha'),
        ],
        lastName: [
          v => !!v || this.$t('addEdituser.lastNameRequired'),
          v => this.onlyAlpha(v) || this.$t('addEdituser.lastNameOllyAlpha'),
        ],
        countryId: [v => !!v || this.$t('activationKey.countryRequired')],
        state: [v => !!v || this.$t(`${isKenyaClient() ? 'County is required' : 'stateRequired'}`)],
      },
    };
  },
  computed: {
    factoryPlanItems() {
      return [
        { text: 'Factory Admin', value: 'factory_admin' },
        { text: 'Factory Clerk', value: 'factory_clerk' },
      ];
    },
  },
  created() {
    this.countries = getCountries();
    this.applyDefaultCountry();
  },
  watch: {
    dialog(val) {
      if (!val) this.resetForm();
    },
  },
  methods: {
    isKenyaClient() {
      return isKenyaClient();
    },
    // Kenya clients: preselect Kenya as the country (and load its counties) so users don't have to.
    applyDefaultCountry() {
      if (!isKenyaClient()) return;
      const kenya = (this.countries || []).find(c => c.code === 'ke' || c.name === 'Kenya');
      if (kenya) {
        this.user.countryId = kenya;
        this.getState(kenya);
      }
    },
    getState(val) {
      if (!val) { this.states = []; return; }
      this.states = val.code === 'ke' ? kenyaCounties : getStates(val.code);
    },
    // Generate a fresh activation key for the selected access level and populate the field.
    async generateKey() {
      const accessLevel = this.user.membershipPlan === 'factory_admin' ? 'admin'
        : this.user.membershipPlan === 'factory_clerk' ? 'clerk' : '';
      if (!accessLevel) {
        this.$notify({ text: 'Please select a Membership Plan first.', type: 'error' });
        return;
      }
      this.keyLoading = true;
      try {
        const res = await BuyingStationService.generateActivationKey(accessLevel);
        if (res.success && res.data && res.data.license_key) {
          this.user.activationKey = res.data.license_key;
          this.user.validityUpTo = res.data.validityUpTo || '';
        } else {
          this.$notify({ title: res.message || 'Could not generate activation key', type: 'error' });
        }
      } catch (e) {
        this.$notify({ title: e?.data?.message || 'Could not generate activation key', type: 'error' });
      } finally {
        this.keyLoading = false;
      }
    },
    onPasswordSetFromDialog({ password }) {
      this.isSetPasswordDialogOpen = false;
      this.user.password = password;
    },
    onPasswordSetCancel() {
      this.isSetPasswordDialogOpen = false;
    },
    closeDialog() {
      this.$emit('close');
    },
    resetForm() {
      this.user = {
        membershipPlan: '', activationKey: '', firstName: '', lastName: '', email: '', mobile: '',
        countryId: null, stateId: null, district: '', village: '', address: '', userTribe: '',
        website: '', validityUpTo: '', status: 1, userRole: 'Farmer', password: '',
      };
      this.errors = { email: '', mobile: '' };
      this.applyDefaultCountry();
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    saveUser() {
      this.errors = { email: '', mobile: '' };
      if (!this.user.email.trim() && !this.user.mobile.trim()) {
        this.errors.email = 'Email or mobile number is required.';
        this.errors.mobile = 'Email or mobile number is required.';
        return;
      }
      // A temporary password is required so the login-credentials email can be sent to the new
      // factory admin/clerk. Block creation until one is set via "Set Temporary Password".
      if (!this.user.password || !this.user.password.trim()) {
        this.$notify({ title: this.$t('memberDataSection.setTemporaryPassword'), text: 'Please set a temporary password before creating the user.', type: 'error' });
        return;
      }
      if (!this.$refs.form.validate()) return;
      this.startLoading();

      const data = { ...this.user };
      const membershipPlanType = data.membershipPlan;
      delete data.membershipPlan;
      delete data.userRole;
      delete data.validityUpTo;

      const countryObj = this.user.countryId || null;
      data.country = countryObj?.name || null;
      data.countryId = countryObj?.name || null;
      data.stateId = this.user.stateId?.name || this.user.stateId || null;
      data.countryIsoCode = countryObj?.code || null;
      data.countryCode = countryObj?.dial_code || null;
      data.active = parseInt(this.user.status, 10);
      if (!data.email || !data.email.trim().length) delete data.password;

      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== null && data[key] !== undefined) formData.append(key, data[key]);
      });
      formData.append('buyingStationId', this.buyingStationId);
      const accessLevel = membershipPlanType === 'factory_admin' ? 'admin'
        : membershipPlanType === 'factory_clerk' ? 'clerk' : '';
      if (accessLevel) formData.append('accessLevel', accessLevel);

      UserService.addAppUserBuyingStation(formData)
        .then((res) => {
          this.stopLoading();
          if (res.success) {
            this.$emit('userAdded', { success: true, message: res.message, user: res.data });
            this.closeDialog();
          } else {
            this.$notify({ title: res.message || 'User creation unsuccessful', type: 'error' });
          }
        })
        .catch((err) => {
          this.stopLoading();
          this.$notify({ title: err?.data?.message || 'User creation unsuccessful, please revisit the form', type: 'error' });
        });
    },
  },
};
</script>

<style scoped>
.custom-card {
  background: #ffffff !important;
  box-shadow: 0px 30px 30px rgba(106, 119, 132, 0.41) !important;
}
</style>
